<template>
    <div
        class="price-type-select"
        :class="{ 'price-type-select--flush': flush }"
        v-click-outside-pts="closeDropdown"
    >
        <button
            type="button"
            class="price-type-btn"
            @click="dropdownOpen = !dropdownOpen"
        >
            <div class="price-type-icon" :class="selected.iconClass">
                <span v-html="selected.icon"></span>
            </div>
            <div class="price-type-info">
                <div class="price-type-name">{{ selected.label }}</div>
                <div class="price-type-desc">{{ selected.desc }}</div>
            </div>
            <svg
                class="chevron"
                :class="{ 'chevron--open': dropdownOpen }"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
        </button>

        <div v-if="dropdownOpen" class="price-type-dropdown">
            <div
                v-for="opt in options"
                :key="opt.value"
                class="price-type-option"
                :class="{ 'price-type-option--active': value === opt.value }"
                @mousedown.prevent="select(opt.value)"
            >
                <div class="price-type-icon" :class="opt.iconClass">
                    <span v-html="opt.icon"></span>
                </div>
                <div class="price-type-info">
                    <div class="price-type-name">{{ opt.label }}</div>
                    <div class="price-type-desc">{{ opt.desc }}</div>
                </div>
                <svg
                    v-if="value === opt.value"
                    class="check-icon"
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#13B98C"
                    stroke-width="2.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
// Vsetky mozne typy ceny v offer. Hodnoty su zhodne s entry/sub_entry type_cena.
// label/desc su i18n kluce (offer.price_type_*), prelozia sa v computed cez $t.
const PRICE_TYPE_DEFS = {
    0: {
        value: 0,
        labelKey: "offer.price_type_hourly_label",
        descKey: "offer.price_type_hourly_desc",
        iconClass: "card-icon--blue",
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><polyline points="12 7 12 12 15 14"></polyline></svg>',
    },
    1: {
        value: 1,
        labelKey: "offer.price_type_range_label",
        descKey: "offer.price_type_range_desc",
        iconClass: "card-icon--purple",
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><polyline points="7 8 3 12 7 16"></polyline><polyline points="17 8 21 12 17 16"></polyline></svg>',
    },
    4: {
        value: 4,
        labelKey: "offer.price_type_hour_range_label",
        descKey: "offer.price_type_hour_range_desc",
        iconClass: "card-icon--pink",
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="10" y1="2" x2="14" y2="2"></line><circle cx="12" cy="14" r="8"></circle><line x1="12" y1="14" x2="12" y2="10"></line></svg>',
    },
    2: {
        value: 2,
        labelKey: "offer.price_type_custom_label",
        descKey: "offer.price_type_custom_desc",
        iconClass: "card-icon--orange",
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z"></path><circle cx="7" cy="7" r="1.4"></circle></svg>',
    },
    3: {
        value: 3,
        labelKey: "offer.price_type_subitems_label",
        descKey: "offer.price_type_subitems_desc",
        iconClass: "card-icon--green",
        icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 21 7 12 12 3 7 12 2"></polygon><polyline points="3 12 12 17 21 12"></polyline><polyline points="3 17 12 22 21 17"></polyline></svg>',
    },
};

export default {
    name: "PriceTypeSelect",
    props: {
        value: { default: 0 },
        // pole hodnot type_cena ktore sa maju ponuknut, v pozadovanom poradi
        types: { type: Array, default: () => [0, 1, 4, 2, 3] },
        // flush = splynie s radom inputov (hranate rohy, zladeny border)
        flush: { type: Boolean, default: false },
    },
    directives: {
        "click-outside-pts": {
            bind(el, binding) {
                el._clickOutsidePts = (e) => {
                    if (!el.contains(e.target)) binding.value();
                };
                document.addEventListener("mousedown", el._clickOutsidePts);
            },
            unbind(el) {
                document.removeEventListener("mousedown", el._clickOutsidePts);
            },
        },
    },
    data() {
        return { dropdownOpen: false };
    },
    computed: {
        options() {
            return this.types
                .map((t) => PRICE_TYPE_DEFS[t])
                .filter(Boolean)
                .map((def) => ({
                    ...def,
                    label: this.$t(def.labelKey),
                    desc: this.$t(def.descKey),
                }));
        },
        selected() {
            const v = parseInt(this.value);
            const def = PRICE_TYPE_DEFS[v] || PRICE_TYPE_DEFS[0];
            return {
                ...def,
                label: this.$t(def.labelKey),
                desc: this.$t(def.descKey),
            };
        },
    },
    methods: {
        closeDropdown() {
            this.dropdownOpen = false;
        },
        select(val) {
            this.dropdownOpen = false;
            if (val !== this.value) {
                this.$emit("input", val);
                this.$emit("change", val);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.price-type-select {
    position: relative;
    font-family: "Roboto", system-ui, sans-serif;
}

// flush variant — splynie s radom inputov v offer editore
.price-type-select--flush {
    height: 100%;

    .price-type-btn {
        height: 100%;
        min-height: 56px;
        border: 1px solid #dde3e8;
        border-bottom: 0;
        border-radius: 0;
        padding: 0px 14px;

        &:hover {
            border-color: #dde3e8;
        }
    }
}

.card-icon {
    &--green {
        background: #e6faf4;
        color: #13b98c;
    }
    &--orange {
        background: #fef1e0;
        color: #e08a1e;
    }
    &--blue {
        background: #e7f0fd;
        color: #2f7be0;
    }
    &--purple {
        background: #ecedfd;
        color: #5b5fe0;
    }
    &--pink {
        background: #fde9ee;
        color: #e1466f;
    }
}

.price-type-btn {
    width: 100%;
    min-height: 56px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border: 1px solid #e2e6ec;
    border-radius: 9px;
    padding: 0px 12px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: border-color 0.15s;

    &:hover {
        border-color: #cfd5de;
    }

    .price-type-icon {
        width: 34px;
        height: 34px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
        svg {
            width: 17px;
            height: 17px;
        }
    }
}

.price-type-info {
    flex: 1;
    min-width: 0;
}
.price-type-name {
    font-weight: 700;
    font-size: 13px;
    color: #2b3240;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.price-type-desc {
    font-size: 11px;
    color: #8a93a6;
    margin-top: 1px;
    line-height: 1.25;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.chevron {
    flex: none;
    color: #a6aeba;
    transition: transform 0.2s;
    &--open {
        transform: rotate(180deg);
    }
}

.price-type-dropdown {
    position: absolute;
    top: calc(100% + 0px);
    left: 0;
    right: 0;
    z-index: 50;
    min-width: 280px;
    background: #fff;
    border: 1px solid #eaedf2;
    border-radius: 0px;
    box-shadow: 0 12px 30px rgba(22, 32, 52, 0.14);
    padding: 5px;
}

.price-type-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 9px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.12s;

    &:hover {
        background: #f4f7fb;
    }
    &--active {
        background: #f4f7fb;
    }

    .price-type-icon {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
        svg {
            width: 16px;
            height: 16px;
        }
    }
}

.check-icon {
    flex: none;
    margin-left: auto;
}
</style>
